// =====================================================================
//  BITÁCORA
//
//  Todos los cambios, del más reciente al más antiguo, con quién los hizo.
//
//  Es inmutable en el motor: los triggers rechazan cualquier UPDATE o
//  DELETE, y los privilegios correspondientes están retirados para todos
//  los roles. Ni el máster puede corregirla. La única vía de escritura es
//  el trigger que la llena.
//
//  El correo queda desnormalizado en la fila a propósito, para que el
//  registro sobreviva al borrado del usuario.
// =====================================================================

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBitacora, useConcesiones } from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, Vacio } from '../componentes/ui'
import { Pagina } from '../App'
import { fechaHora, fmt } from '../dominio/formato'

const CAMPOS: Record<string, string> = {
  estado: 'Estado',
  interes: 'Interés',
  postura_max: 'Postura máxima',
  superficie_ha: 'Superficie (ha)',
  fecha_pago_patente: 'Fecha de pago',
  monto_pagado: 'Monto pagado',
  monto_adjudicacion: 'Monto de adjudicación',
  adjudicatario: 'Adjudicatario',
  notas: 'Notas',
}

/** Los campos que son montos se muestran formateados. */
const ES_MONTO = new Set(['postura_max', 'monto_pagado', 'monto_adjudicacion'])

export function Bitacora() {
  const bitacora = useBitacora()
  const concesiones = useConcesiones()
  const [, setParams] = useSearchParams()
  const [filtro, setFiltro] = useState('')
  const [campo, setCampo] = useState('')

  const porId = useMemo(
    () => new Map((concesiones.data ?? []).map((c) => [c.id, c])),
    [concesiones.data],
  )

  const filas = useMemo(() => {
    const todas = bitacora.data ?? []
    const busca = filtro.trim().toLowerCase()
    return todas.filter((b) => {
      if (campo && b.campo !== campo) return false
      if (!busca) return true
      const c = b.concesion_id ? porId.get(b.concesion_id) : null
      return `${c?.nombre ?? ''}${b.usuario_email ?? ''}${b.valor_nuevo ?? ''}`
        .toLowerCase()
        .includes(busca)
    })
  }, [bitacora.data, filtro, campo, porId])

  if (bitacora.isError) return <ErrorCarga error={bitacora.error} />
  if (bitacora.isLoading) return <Cargando />

  const valor = (b: { campo: string | null }, v: string | null) => {
    if (v === null || v === '') return '—'
    if (b.campo && ES_MONTO.has(b.campo)) return fmt(v)
    if (b.campo === 'notas') return v.length > 90 ? v.slice(0, 90) + '…' : v
    return v
  }

  const campos = [...new Set((bitacora.data ?? []).map((b) => b.campo).filter(Boolean))] as string[]

  return (
    <>
      <Pagina
        titulo="Bitácora"
        sub="Todos los cambios registrados, del más reciente al más antiguo. La escribe el motor, no la aplicación, y no se puede editar ni borrar."
      />

      <div className="toolbar">
        <input
          type="search"
          placeholder="Buscar concesión, persona o valor…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        <select value={campo} onChange={(e) => setCampo(e.target.value)}>
          <option value="">Todos los campos</option>
          {campos.map((k) => (
            <option key={k} value={k}>
              {CAMPOS[k] ?? k}
            </option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: '6px 10px 0' }}>
        <table>
          <thead>
            <tr>
              <th>Concesión</th>
              <th>Campo</th>
              <th>Antes</th>
              <th>Después</th>
              <th>Quién</th>
              <th>Cuándo</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 ? (
              <Vacio columnas={6}>
                {bitacora.data?.length
                  ? 'Ningún movimiento coincide con el filtro.'
                  : 'Todavía no hay movimientos. Abre una ficha y cambia su estado o interés.'}
              </Vacio>
            ) : (
              filas.map((b) => {
                const c = b.concesion_id ? porId.get(b.concesion_id) : null
                return (
                  <tr
                    key={b.id}
                    onClick={() => c && setParams({ ficha: c.rol_nacional })}
                    style={{ cursor: c ? 'pointer' : 'default' }}
                  >
                    <td>
                      <div className="nm">{c?.nombre ?? `Concesión ${b.concesion_id ?? '—'}`}</div>
                      {c ? <div className="sub mono">{c.rol_nacional}</div> : null}
                    </td>
                    <td className="sub">{b.campo ? (CAMPOS[b.campo] ?? b.campo) : b.accion}</td>
                    <td className="sub">{valor(b, b.valor_anterior)}</td>
                    <td>{valor(b, b.valor_nuevo)}</td>
                    <td className="sub mono">{b.usuario_email ?? 'sistema'}</td>
                    <td className="sub">{fechaHora(b.creado)}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        <div style={{ padding: '11px 4px', fontSize: 12, color: 'var(--txt3)' }}>
          {filas.length} de {bitacora.data?.length ?? 0} movimientos
        </div>
      </div>

      <Alerta tono="info" estilo={{ marginTop: 14 }}>
        Las filas sin persona («sistema») son cambios hechos desde la base de datos o por un proceso
        de servidor, sin sesión de usuario. Los cambios hechos desde el portal siempre llevan el
        correo de quien los hizo: el motor lo toma de la sesión, no de lo que manda el navegador.
      </Alerta>
    </>
  )
}
